import {
  BOTTOM_NAV,
  getServicesNav,
  type HudNavItem,
  IconComponent,
  MIDDLE_NAV,
  moreButton,
  NonClickableHudNavItem,
  TOP_NAV,
} from '../model/routes';
import {
  DesktopAddButtonWrapper,
  DesktopHudBottom,
  DesktopHudLogo,
  DesktopHudMiddle,
  DesktopHudTitle,
  DesktopHudTop,
  DesktopHudWrap,
  DesktopOtherButtonWrapper,
} from './styles';
import {Logo} from 'shared/ui/icons/logo';
import {HudButton} from './HudButton/HudButton';
import {Button} from '@atomaro/ui-kit';
import {CollapseButton} from 'widgets/hud/ui/CollapseButton/CollapseButton';
import {ApiariesPicker} from 'widgets/hud/ui/ApiariesPicker';
import ApiariesModal from 'widgets/apiariesModal/ui/ApiariesModal';
import {SearchModal} from 'widgets/searchModal/ui/SearchModal';
import {NotificationsModal} from 'widgets/notificationsModal';
import {LogoutModal, ProfileModal, TgContactModal} from 'widgets/profile';
import {SettingsModal} from 'widgets/settingsModal';
import {OtherModal} from 'widgets/otherModal';
import React, {useContext, useMemo, useRef} from 'react';
import {useResizeObserverHeight} from 'widgets/hud/lib/useResizeObserver';
import {getOtherModalCount} from 'widgets/hud/lib/hudOverflow';
import {useHudCollapse} from 'widgets/hud/model/collapseStore.ts';
import {AddLarge} from 'shared/ui/icons';
import {useDiaryStore} from 'entities/diary';
import {Profile} from 'shared/ui/icons/hudIcons/profile.tsx';
import {useUserStore} from 'entities/user';
import {OurServicesModal} from 'widgets/ourServicesModal';
import {ConfigContext} from '../../../app/App.tsx';

const OTHER_MODAL_OFFSET_PX = 30;

export const DesktopHud: React.FC = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const otherBtnRef = useRef<HTMLDivElement | null>(null);
  const servicesRef = useRef<HTMLDivElement | null>(null);
  const middleHeight = useResizeObserverHeight(containerRef);
  const appConfig = useContext(ConfigContext);
  const iAmAdmin = useUserStore((s) => s.iAmAdmin);
  const userInfo = useUserStore((state) => state.userInfo);

  const overflowCount = useMemo(() => {
    const cnt = getOtherModalCount(middleHeight);
    return Math.max(0, Math.min(cnt, MIDDLE_NAV.length));
  }, [middleHeight]);

  const visibleMiddle = useMemo(
    () => MIDDLE_NAV.slice(0, MIDDLE_NAV.length - overflowCount),
    [overflowCount]
  );
  const overflowMiddle = useMemo(() => MIDDLE_NAV.slice(-overflowCount), [overflowCount]);

  const collapsed = useHudCollapse((s) => s.collapsed);
  const setIsNewEntrieOpen = useDiaryStore((s) => s.setIsNewEntrieOpen);

  const avatarId = userInfo?.profile?.avatar?.id;
  const imgSrc = `/ecpp/attachments/files?attachmentId=${avatarId}`;
  const IconComponent: IconComponent = () => <Profile imgSrc={avatarId ? imgSrc : ''} />;

  const profileButton = {
    id: 'profile',
    label: `${userInfo?.name.first} ${userInfo?.name.last}`,
    icon: IconComponent,
    iconActive: IconComponent,
    isClickable: true,
    iconActiveFill: 'rgba(255, 79, 18, 1)',
  };

  const servicesList = getServicesNav(appConfig);

  return (
    <>
      <DesktopHudWrap
        $collapsed={collapsed}
        ref={containerRef}
        id="mainNav"
        aria-label="Main navigation"
      >
        <DesktopHudTop $collapsed={collapsed}>
          <DesktopHudLogo to="/" $collapsed={collapsed}>
            <Logo />
            {!collapsed && <DesktopHudTitle>Цифровой улей</DesktopHudTitle>}
          </DesktopHudLogo>

          {TOP_NAV.map((item) => {
            if (item.adminOnly && !iAmAdmin) return <></>;

            return <HudButton key={item.id} item={item} aria-label={item.label} />;
          })}
        </DesktopHudTop>

        <DesktopHudMiddle $collapsed={collapsed}>
          <ApiariesPicker />
          {visibleMiddle.map((item) => {
            if (item.adminOnly && !iAmAdmin) return <></>;

            return <HudButton key={item.id} item={item} aria-label={item.label} />;
          })}

          {overflowCount > 0 && (
            <DesktopOtherButtonWrapper ref={otherBtnRef}>
              <HudButton item={moreButton} aria-label={'Ещё'} />
            </DesktopOtherButtonWrapper>
          )}

          <DesktopAddButtonWrapper $collapsed={collapsed}>
            <Button
              label={!collapsed && 'Новая запись'}
              variant={'primary'}
              size={'m'}
              iconPrefix={<AddLarge />}
              onClick={() => setIsNewEntrieOpen(true)}
            />
          </DesktopAddButtonWrapper>
        </DesktopHudMiddle>

        <DesktopHudBottom $collapsed={collapsed}>
          {BOTTOM_NAV.map((item) => {
            if (item.adminOnly && !iAmAdmin) return <></>;

            if (item.id === 'services') {
              return (
                <HudButton key={item.id} item={item} aria-label={item.label} ref={servicesRef} />
              );
            }
            return <HudButton key={item.id} item={item} aria-label={item.label} />;
          })}
          <HudButton item={profileButton as HudNavItem} aria-label={profileButton.label} />
        </DesktopHudBottom>

        <CollapseButton />
      </DesktopHudWrap>
      <ApiariesModal />
      <SearchModal />
      <SettingsModal />
      <NotificationsModal />
      <ProfileModal />
      <TgContactModal />
      <LogoutModal />
      <OurServicesModal
        items={servicesList as NonClickableHudNavItem[]}
        anchorRef={servicesRef}
        offsetX={OTHER_MODAL_OFFSET_PX}
      />
      {overflowCount > 0 && (
        <OtherModal
          items={overflowMiddle}
          anchorRef={otherBtnRef}
          offsetX={OTHER_MODAL_OFFSET_PX}
        />
      )}
    </>
  );
};
